import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["sidebar", "overlay", "toggleButton"]
  static values = { 
    breakpoint: { type: Number, default: 768 },
    expanded: { type: Boolean, default: false }
  }

  connect() {
    this.setupEventListeners()
    this.updateVisibility()
    this.handleResize()
  }

  disconnect() {
    this.removeEventListeners()
  }

  setupEventListeners() {
    this.handleResizeBound = this.handleResize.bind(this)
    this.handleKeydownBound = this.handleKeydown.bind(this)
    this.handleClickOutsideBound = this.handleClickOutside.bind(this)
    
    window.addEventListener('resize', this.handleResizeBound)
    document.addEventListener('keydown', this.handleKeydownBound)
    
    // Setup overlay click handler if overlay exists
    if (this.hasOverlayTarget) {
      this.overlayTarget.addEventListener('click', this.close.bind(this))
    }
  }

  removeEventListeners() {
    window.removeEventListener('resize', this.handleResizeBound)
    document.removeEventListener('keydown', this.handleKeydownBound)
  }

  toggle() {
    if (this.expandedValue) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    this.expandedValue = true
    this.updateVisibility()
    if (this.hasSidebarTarget) {
      this.sidebarTarget.classList.remove("hidden")
    }
    
    // Focus management - focus first focusable element in sidebar
    if (this.hasSidebarTarget) {
      const firstFocusable = this.sidebarTarget.querySelector('input, select, button, [tabindex]:not([tabindex="-1"])')
      if (firstFocusable) {
        setTimeout(() => firstFocusable.focus(), 100)
      }
    }
    
    // Add click outside listener when open on mobile
    if (this.isMobile()) {
      setTimeout(() => {
        document.addEventListener('click', this.handleClickOutsideBound)
      }, 100)
    }
    
    // Announce to screen readers
    this.announceFilterState('Filter panel opened')
  }

  close() {
    this.expandedValue = false
    this.updateVisibility()
    
    // Return focus to toggle button
    if (this.hasToggleButtonTarget) {
      this.toggleButtonTarget.focus()
    }
    
    // Remove click outside listener
    document.removeEventListener('click', this.handleClickOutsideBound)
    
    // Hide the sidebar panel
    if (this.hasSidebarTarget) {
      this.sidebarTarget.classList.add("hidden")
    }
    
    // Announce to screen readers
    this.announceFilterState('Filter panel closed')
  }

  handleResize() {
    // For the new filter design, only hide the sidebar panel, not the entire component
    if (this.hasSidebarTarget) {
      if (this.isMobile()) {
        if (this.expandedValue) {
          // Keep expanded state but ensure proper mobile styling
          this.sidebarTarget.classList.remove("hidden")
          this.updateVisibility()
        } else {
          this.sidebarTarget.classList.add("hidden")
        }
      } else {
        // On desktop, only show sidebar if manually expanded
        if (!this.expandedValue) {
          this.sidebarTarget.classList.add("hidden")
        } else {
          this.sidebarTarget.classList.remove("hidden")
        }
      }
    }
    
    this.updateVisibility()
  }

  handleKeydown(event) {
    // Close on Escape key
    if (event.key === 'Escape' && this.expandedValue && this.isMobile()) {
      event.preventDefault()
      this.close()
    }
  }

  handleClickOutside(event) {
    // Only handle on mobile when expanded
    if (!this.isMobile() || !this.expandedValue) return
    
    // Check if click is outside the sidebar panel
    if (this.hasSidebarTarget && 
        !this.sidebarTarget.contains(event.target) && 
        !this.isToggleButton(event.target)) {
      this.close()
    }
  }

  updateVisibility() {
    const isMobile = this.isMobile()
    
    // For the new design, toggle button should always be visible
    if (this.hasToggleButtonTarget) {
      this.toggleButtonTarget.setAttribute('aria-expanded', this.expandedValue.toString())
    }
    
    // Update overlay visibility (only on mobile)
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.toggle('hidden', !isMobile || !this.expandedValue)
    }
    
    // Update sidebar classes for mobile/desktop states
    if (isMobile) {
      this.element.classList.toggle('mobile-expanded', this.expandedValue)
      this.element.classList.toggle('mobile-collapsed', !this.expandedValue)
    } else {
      this.element.classList.remove('mobile-expanded', 'mobile-collapsed')
    }
  }

  isMobile() {
    return window.innerWidth < this.breakpointValue
  }

  isToggleButton(element) {
    return this.hasToggleButtonTarget && 
           (this.toggleButtonTarget === element || this.toggleButtonTarget.contains(element))
  }

  announceFilterState(message) {
    // Create or update live region for screen reader announcements
    let liveRegion = document.getElementById('filter-sidebar-status')
    if (!liveRegion) {
      liveRegion = document.createElement('div')
      liveRegion.id = 'filter-sidebar-status'
      liveRegion.className = 'sr-only'
      liveRegion.setAttribute('aria-live', 'polite')
      liveRegion.setAttribute('aria-atomic', 'true')
      document.body.appendChild(liveRegion)
    }
    
    liveRegion.textContent = message
    
    // Clear message after announcement
    setTimeout(() => {
      liveRegion.textContent = ''
    }, 1000)
  }
}